/**
 * Typed client methods for the BEAM calendar / schedule-event surface (WO-22).
 *
 * Routes (beam-backend calendarRouter.ts):
 *   GET    /api/schedule-events            ?from&to&event_type&assigned_to → ScheduleEventDto[]
 *   GET    /api/schedule-events/:id                                         → ScheduleEventDto
 *   POST   /api/schedule-events            { CreateScheduleEventRequest }   → ScheduleEventDto
 *   PATCH  /api/schedule-events/:id        { UpdateScheduleEventRequest }   → ScheduleEventDto
 *   DELETE /api/schedule-events/:id
 *
 * CASING: snake_case — these endpoints return raw Prisma rows (no serializer), so the
 * wire JSON and the @beam/api-types DTOs are snake_case. Responses parsed with the zod
 * schemas (fail loudly on drift). Bearer token via src/api/client.ts. No PII in URLs.
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { get, post, patch, del } from './client';
import { ScheduleEventDto } from '@beam/api-types';
import type {
  CreateScheduleEventRequest,
  UpdateScheduleEventRequest,
  ScheduleEventType,
} from '@beam/api-types';

export interface ListScheduleEventsParams {
  /** ISO datetime lower bound (inclusive). */
  from?: string;
  /** ISO datetime upper bound (inclusive). */
  to?: string;
  event_type?: ScheduleEventType;
  assigned_to?: string;
}

/** List schedule events for the org, optionally bounded by range / type / assignee. */
export async function listScheduleEvents(
  params: ListScheduleEventsParams = {},
): Promise<ScheduleEventDto[]> {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.event_type) qs.set('event_type', params.event_type);
  if (params.assigned_to) qs.set('assigned_to', params.assigned_to);
  const query = qs.toString();
  const raw = await get<unknown>(
    `/api/schedule-events${query ? '?' + query : ''}`,
  );
  return (raw as unknown[]).map((item) => ScheduleEventDto.parse(item));
}

/** Fetch a single schedule event. */
export async function getScheduleEvent(id: string): Promise<ScheduleEventDto> {
  const raw = await get<unknown>(`/api/schedule-events/${id}`);
  return ScheduleEventDto.parse(raw);
}

/** Create a schedule event. */
export async function createScheduleEvent(
  body: CreateScheduleEventRequest,
): Promise<ScheduleEventDto> {
  const raw = await post<unknown>('/api/schedule-events', body);
  return ScheduleEventDto.parse(raw);
}

/** Patch a schedule event (reschedule = change starts_at/ends_at; reassign, etc.). */
export async function updateScheduleEvent(
  id: string,
  body: UpdateScheduleEventRequest,
): Promise<ScheduleEventDto> {
  const raw = await patch<unknown>(`/api/schedule-events/${id}`, body);
  return ScheduleEventDto.parse(raw);
}

/** Delete a schedule event. */
export async function deleteScheduleEvent(id: string): Promise<void> {
  await del<unknown>(`/api/schedule-events/${id}`);
}
